// src/components/auth/AuthShell.jsx
// Layout split brand + form dùng chung cho login / register / forgot / reset password.

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Briefcase, BookOpen } from "lucide-react";
import logo from "../../assets/images/logo/logo-ima-hec.png";
import logoMark from "../../assets/images/logo/logo-ima.png";
import { getLatestVersion } from "../../api/system/appVersions";

const MODULE_HINTS = [
  { icon: Users, label: "CRM" },
  { icon: Briefcase, label: "HRM" },
  { icon: BookOpen, label: "E-Learning" },
];

export default function AuthShell({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
  brandDescription = "Hệ thống quản trị vận hành trung tâm giáo dục — CRM, nhân sự, học tập và tài chính trên một nền tảng.",
  maxWidthClass = "max-w-[400px]",
}) {
  const [appVersion, setAppVersion] = useState(null);

  useEffect(() => {
    getLatestVersion()
      .then((latest) => {
        if (latest) setAppVersion(latest.version_code);
      })
      .catch(() => {});
  }, []);

  const versionLabel = appVersion?.slice(1) || "0.0.0";

  return (
    <div className="flex min-h-screen w-screen overflow-hidden bg-[#faf7f5]">
      {/* Brand panel */}
      <aside className="relative hidden w-[52%] flex-col justify-between overflow-hidden bg-[#8f1219] px-12 py-10 text-white lg:flex xl:px-16">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 20% 10%, rgba(255,255,255,0.14), transparent 55%), radial-gradient(ellipse 70% 50% at 90% 90%, rgba(0,0,0,0.28), transparent 50%), linear-gradient(165deg, #b91c1c 0%, #8f1219 42%, #5c0a10 100%)",
          }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full border border-white/15"
          animate={{ scale: [1, 1.06, 1], opacity: [0.35, 0.55, 0.35] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-white/5"
          animate={{ y: [0, -18, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-10"
        >
          <p className="text-sm font-bold uppercase tracking-[0.28em] text-white/80">
            IMA Việt Nam
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="relative z-10 max-w-lg"
        >
          <img
            src={logoMark}
            alt="IMA Việt Nam"
            className="mb-8 h-28 w-28 rounded-full bg-white object-contain p-1.5 shadow-2xl shadow-black/30"
          />
          <h1 className="text-5xl font-extrabold tracking-tight xl:text-6xl">IMA VIỆT NAM</h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-white/75 xl:text-lg">
            {brandDescription}
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            {MODULE_HINTS.map(({ icon: Icon, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + i * 0.08 }}
                className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur-sm"
              >
                <Icon size={15} className="opacity-90" />
                {label}
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="relative z-10 text-xs text-white/50"
        >
          Powered by <span className="font-semibold text-white/70">Hameno Software</span>
          {" · "}Version {versionLabel}
        </motion.p>
      </aside>

      {/* Form panel */}
      <main className="relative flex flex-1 flex-col items-center justify-center px-6 py-12 sm:px-10">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 80% 0%, rgba(185,28,28,0.08), transparent 55%)",
          }}
        />

        <motion.div
          initial={{ opacity: 0, x: 28 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.45 }}
          className={`relative z-10 w-full ${maxWidthClass}`}
        >
          <div className="mb-10 flex flex-col items-center lg:hidden">
            <img src={logo} alt="IMA Việt Nam" className="h-12 w-auto" />
            <p className="mt-3 text-sm font-bold uppercase tracking-[0.2em] text-[#8f1219]">
              IMA VIỆT NAM
            </p>
          </div>

          {(eyebrow || title || subtitle) && (
            <div className="mb-8">
              {eyebrow && (
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f1219]/60">
                  {eyebrow}
                </p>
              )}
              {title && (
                <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-stone-900">
                  {title}
                </h2>
              )}
              {subtitle && <p className="mt-2 text-sm text-stone-500">{subtitle}</p>}
            </div>
          )}

          {children}

          {footer && <div className="mt-8">{footer}</div>}

          <p className="mt-10 text-center text-xs text-stone-400 lg:hidden">
            Powered by <span className="font-semibold">Hameno Software</span>
            {" · "}Version {versionLabel}
          </p>
        </motion.div>
      </main>
    </div>
  );
}

/** Class input chuẩn cho các form auth */
export const authInputClass =
  "w-full rounded-xl border border-stone-200 bg-white py-3 px-4 text-sm text-stone-900 outline-none transition focus:border-[#8f1219] focus:ring-4 focus:ring-[#8f1219]/10";

export const authInputWithIconClass =
  "w-full rounded-xl border border-stone-200 bg-white py-3 pl-11 pr-4 text-sm text-stone-900 outline-none transition focus:border-[#8f1219] focus:ring-4 focus:ring-[#8f1219]/10";

export const authPrimaryBtnClass =
  "group mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[#8f1219] py-3.5 text-sm font-bold text-white shadow-lg shadow-[#8f1219]/20 transition hover:bg-[#a3161e] disabled:cursor-not-allowed disabled:opacity-70";
